import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { NodeType } from '../../../types/erd';

export function useNodeTypeFilter(nodeTypes: NodeType[]): {
  visibleTypes: Set<string>;
  visibleTypesRef: React.MutableRefObject<Set<string>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  filteredNodeTypes: NodeType[];
  toggleNodeType: (typeId: string) => void;
  selectAllTypes: () => void;
  deselectAllTypes: () => void;
} {
  const previousNodeIdsRef = useRef<string>('');
  const [visibleTypes, setVisibleTypes] = useState<Set<string>>(() => new Set());
  const visibleTypesRef = useRef<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const isVisibleTypesInitialized = useRef(false);

  useEffect(() => {
    if (nodeTypes.length === 0) return;

    const currentNodeIds = nodeTypes.map(t => t.id).sort().join(',');

    if (!isVisibleTypesInitialized.current || (previousNodeIdsRef.current !== currentNodeIds)) {
      isVisibleTypesInitialized.current = true;
      previousNodeIdsRef.current = currentNodeIds;

      const allTypes = new Set(nodeTypes.map(t => t.id));
      setVisibleTypes(allTypes);
      visibleTypesRef.current = allTypes;
    }
  }, [nodeTypes]);

  useEffect(() => {
    visibleTypesRef.current = visibleTypes;
  }, [visibleTypes]);

  const toggleNodeType = useCallback((typeId: string) => {
    setVisibleTypes((prev) => {
      const updated = new Set(prev);
      if (updated.has(typeId)) {
        updated.delete(typeId);
      } else {
        updated.add(typeId);
      }
      return updated;
    });
  }, []);

  const selectAllTypes = useCallback(() => {
    setVisibleTypes(new Set(nodeTypes.map(t => t.id)));
  }, [nodeTypes]);

  const deselectAllTypes = useCallback(() => {
    setVisibleTypes(new Set());
  }, []);

  const filteredNodeTypes = useMemo(() => {
    if (!searchQuery.trim()) return nodeTypes;
    const query = searchQuery.toLowerCase();
    return nodeTypes.filter(type => type.label.toLowerCase().includes(query));
  }, [searchQuery, nodeTypes]);

  return {
    visibleTypes,
    visibleTypesRef,
    searchQuery,
    setSearchQuery,
    filteredNodeTypes,
    toggleNodeType,
    selectAllTypes,
    deselectAllTypes,
  };
}
